import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { AuthLayout } from '../../ui/AuthLayout/AuthLayout';
import { useNavigate, useSearchParams } from 'react-router';
import { useEffect } from 'react';
import { getSelectBillingPlanPath } from '@/bundle/Auth/AdminOnboardingFlow/SelectBillingPlanPage/path';
import { AuthHeading } from '@/bundle/Auth/ui/AuthHeading/AuthHeading';
import { getDashboardRedirectPath } from '../../LoginFlow/LoginPage/const/const';
import { useQuery } from '@tanstack/react-query';
import { LayoutSpinner } from '@/bundle/Layouts/styles';
import { SubscriptionPlanInfoCard } from './ui/SubscriptionPlanInfoCard';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { Notification } from '@/components/Notification/Notification';
import { NavigationLink } from '@/components/NavigationLink/NavigationLink';
import { SubscriptionCheckoutWidget } from '@/bundle/shared/components/Checkout/Subscription/SubscriptionCheckoutWidget';
import { getBillingPlanDetails } from '@/bundle/shared/api';
import { USER_ROLE_MAP } from '@/const/user';
import { useUser } from '@/context/userContext';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { SubscriptionSuccessSnackbarMessage } from './const';
import { SubscriptionCheckoutStep, SubscriptionCheckoutWrapper } from './styles';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { BILLING_FREQUENCY_LABEL } from '@/const/shared';
import { AdminOnboardingStepper } from '../ui/AdminOnboardingStepper';
import { getAdminOnboardingFlow } from '@/bundle/shared/hooks/useAdminOnboardingFlowFromAuthToken';

export const SubscriptionCheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan_id');
  const { user, refetchUser } = useUser();

  const { data: planDetailsData, isPending: isPendingPlanDetails } = useQuery({
    queryKey: ['get_billing_plan_details', planId],
    queryFn: () => getBillingPlanDetails(planId),
    enabled: !!planId,
  });

  const plan = planDetailsData?.body;

  const navigateToDashboard = async () => {
    const dashboardUrl = getDashboardRedirectPath(USER_ROLE_MAP.MASTER_ADMIN);

    amplitudeService.logEvent(AMPLITUDE_EVENTS.SubscriptionPurchasedSuccess, {
      plan_type: plan?.name,
      frequency: BILLING_FREQUENCY_LABEL[plan?.billing_frequency],
    });
    await refetchUser();

    navigate(dashboardUrl, { replace: true });
    showSnackbar(<SubscriptionSuccessSnackbarMessage />);
  };

  useEffect(() => {
    if (!planId) {
      navigate(getSelectBillingPlanPath());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const error = getResponseError(planDetailsData?.error);
  const { isOnboardingFlow, signUpStep } = getAdminOnboardingFlow(user);

  return (
    <AuthLayout position='top' banner={isOnboardingFlow && <AdminOnboardingStepper signUpStep={signUpStep} />}>
      <AuthHeading
        header='Complete Your Subscription'
        subHeader={
          <>
            Thank you for choosing the{' '}
            <Typography as='span' color={COLORS.grey[900]} fontWeight='medium' variant='subTitleSmall'>
              {plan?.name}
            </Typography>{' '}
            Plan.
            <br />
            Review details below, then continue to secure payment.
          </>
        }
      />

      {error && (
        <>
          <Notification variant='error' marginBottom='16px'>
            {error}
          </Notification>
          <NavigationLink to={getSelectBillingPlanPath()}>{'< Return to Select Billing Plan'}</NavigationLink>
        </>
      )}
      {isPendingPlanDetails && <LayoutSpinner />}

      {plan && (
        <SubscriptionCheckoutWrapper>
          <SubscriptionCheckoutStep>
            <SubscriptionCheckoutWidget planId={plan.id} onSuccess={navigateToDashboard} />
          </SubscriptionCheckoutStep>
          <SubscriptionPlanInfoCard plan={plan} />
        </SubscriptionCheckoutWrapper>
      )}
    </AuthLayout>
  );
};
